/**
 * A flag de lançamento de Clubes, no cliente.
 *
 * UMA LEITURA POR SESSÃO. O `getDoc` roda na primeira vez que alguém pergunta e
 * a promessa fica guardada no módulo — as outras chamadas recebem a mesma.
 * Sem isso, a aba Início perguntaria a cada montagem, e a aba Início monta
 * muito.
 *
 * `onSnapshot` seria errado aqui por dois motivos: ligar a feature no meio da
 * sessão de alguém faria a aba mudar debaixo do dedo, e um listener num
 * documento global custa uma leitura por escrita nele **vezes** o número de
 * pessoas com o app aberto.
 *
 * FAIL-CLOSED. Erro de rede, permissão negada, documento inexistente: a
 * resposta é `false`. A feature ainda não existe para ninguém, e o estado
 * seguro é continuar não existindo — a aba fica exatamente como está em
 * produção hoje.
 */

import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';

let promessa = null;

/**
 * Lê `CONFIG/CLUBES` e decide se este uid enxerga a feature.
 *
 * @param {string} uid quem está usando o app
 * @returns {Promise<boolean>}
 */
export function clubesHabilitado(uid) {
  if (!promessa) {
    promessa = getDoc(doc(db, 'CONFIG', 'CLUBES'))
      .then((snap) => (snap.exists() ? snap.data() || {} : {}))
      .catch((e) => {
        // Silencioso de propósito: com a flag desligada, `CONFIG/CLUBES` pode
        // nem existir, e um erro vermelho no console de todo mundo seria ruído
        // sobre um estado esperado.
        console.warn('[clubes] CONFIG/CLUBES indisponível:', e?.message);
        // A falha NÃO fica guardada: lida antes do login (permissão negada),
        // ela respondia "desligado" pelo resto da sessão, inclusive depois de
        // a pessoa entrar. A próxima pergunta tenta de novo.
        promessa = null;
        return {};
      });
  }

  return promessa.then((cfg) => {
    // A MESMA leitura estrita do servidor: só o booleano `true` liga. String
    // "true" não liga — se as duas pontas divergissem nisso, a aba apareceria
    // para a pessoa e todas as callables recusariam.
    if (cfg.HABILITADO === true) return true;
    const lista = Array.isArray(cfg.TESTADORES_UIDS) ? cfg.TESTADORES_UIDS : [];
    const u = String(uid || '').trim();
    return !!u && lista.includes(u);
  });
}

/**
 * O CONFIG/CLUBES cru (mesma leitura da sessão). `{}` quando indisponível.
 * Usado para chaves de operação, como PREVIEW_CHAMADA_UIDS.
 * @returns {Promise<object>}
 */
export function configDeClubes(uid) {
  return clubesHabilitado(uid).then(() => (promessa ? promessa : {})).catch(() => ({}));
}

/** Esquece a leitura da sessão. Só para teste e para o logout. */
export function esquecerFlagDeClubes() {
  promessa = null;
}
